import { AccountInfo } from "@azure/msal-browser";
import compact from "lodash/compact";
import intersection from "lodash/intersection";

import { AccessGuardProps } from "@/components/access-guard";
import { ActiveDirectoryRoles } from "@/config/ad-roles";

export interface IAdRole {
    id: string;
    name: string;
}

/**
 * Authorization service for Microsoft Authentication Library and Illuminate tags + permissions.
 */
export class AuthorizationService {
    /**
     * The Active Directory Roles.
     */
    static adRoles = [
        {
            id: ActiveDirectoryRoles.AusNet,
            name: "AusNet",
        },
        {
            id: ActiveDirectoryRoles.Dnsp,
            name: "DNSP",
        },
        {
            id: ActiveDirectoryRoles.Engineering,
            name: "Engineering",
        },
        {
            id: ActiveDirectoryRoles.Jemena,
            name: "Jemena",
        },
        {
            id: ActiveDirectoryRoles.RedEnergy,
            name: "RedEnergy",
        },
    ];

    /**
     * Tests if at least one required AD role is found in the user AD roles.
     * @param adRoleIds The required AD roles IDs.
     * @param userAdRoles The user AD roles.
     * @returns True if a least one AD role match was found.
     */
    static isInAdRole(adRoleIds: string[], userAdRoles: IAdRole[]): boolean;
    /**
     * Tests if the required AD role is found in the user AD roles.
     * @param adRoleId The required AD roles ID.
     * @param userAdRoles The user AD roles.
     * @returns True if a least one AD role match was found.
     */
    static isInAdRole(adRoleId: string, userAdRoles: IAdRole[]): boolean;
    /**
     * Tests if at least one required AD role is found in the user AD roles.
     * @param adRoleIdOrAdRoleIds The required AD roles ID.
     * @param userAdRoles The user AD roles.
     * @returns True if a least one AD role match was found.
     */
    static isInAdRole(
        adRoleIdOrAdRoleIds: string | string[],
        userAdRoles: IAdRole[],
    ): boolean {
        if (typeof adRoleIdOrAdRoleIds === "string") {
            adRoleIdOrAdRoleIds = [adRoleIdOrAdRoleIds];
        }

        const adRoleIds: string[] = adRoleIdOrAdRoleIds;

        const result =
            // If AD role IDs is null or empty then they are authorized.
            !adRoleIds ||
            adRoleIds.length === 0 ||
            // Match any AD role ID:
            // If AD role IDs  contains '*' and the user has an AD role then they are authorized.
            (adRoleIds.findIndex((id) => id === "*") !== -1 &&
                userAdRoles &&
                userAdRoles.length > 0) ||
            // Match any specified AD role ID:
            // If the intersection of the required IDs and user AD role IDs is 1 or more than then they are authorized.
            (userAdRoles &&
                adRoleIds.filter((id) =>
                    userAdRoles.map((r) => r.id).includes(id),
                ).length > 0);

        return result;
    }

    /**
     * Get the AD roles of the user from the route guard properties.
     * @param routeGuardProps The route guard properties.
     * @returns An array of user AD roles.
     */
    static getUserAdRoles(
        routeGuardProps: AccessGuardProps | null | undefined,
    ): IAdRole[];
    /**
     * Get the AD roles of the user from the MSAL account info.
     * @param account The MSAL account info.
     * @returns An array of user AD roles.
     */
    static getUserAdRoles(account: AccountInfo | null | undefined): IAdRole[];
    /**
     * Get the AD roles of the user from the route guard properties or the MSAL account info.
     * @param accountOrRouteGuardProps The route guard properties or the MSAL account info.
     * @returns An array of user AD roles.
     */
    static getUserAdRoles(
        accountOrRouteGuardProps:
            | AccountInfo
            | AccessGuardProps
            | null
            | undefined,
    ): IAdRole[] {
        let adRoles: IAdRole[] = [];
        const idToAdRole = (id: string): IAdRole | undefined =>
            this.adRoles.find((r) => r.id === id);
        const idsToAdRoles = (ids: string[]): (IAdRole | undefined)[] =>
            ids.map(idToAdRole);

        if (
            accountOrRouteGuardProps &&
            "idTokenClaims" in accountOrRouteGuardProps &&
            accountOrRouteGuardProps.idTokenClaims
        ) {
            const account: AccountInfo = accountOrRouteGuardProps;
            const idTokenClaims = account.idTokenClaims;

            if (
                idTokenClaims?.["groups"] &&
                Array.isArray(idTokenClaims["groups"]) &&
                idTokenClaims["groups"].length > 0 &&
                typeof idTokenClaims["groups"][0] === "string"
            ) {
                const adRoleIds: string[] = idTokenClaims["groups"];

                adRoles = compact(idsToAdRoles(adRoleIds));
            }
        }

        return adRoles;
    }

    /**
     * Tests if all required permissions are found in the user permissions.
     * @param permissions The required permissions.
     * @param userPermissions The user permissions.
     */
    static hasPermissions(
        permissions: string[],
        userPermissions: string[],
        selector: "all" | "any",
    ) {
        if (!permissions.length) {
            return true;
        }
        if (selector === "any") {
            return intersection(userPermissions, permissions).length > 0;
        }
        return (
            intersection(userPermissions, permissions).length ===
            permissions.length
        );
    }

    /**
     * Tests if all required access tags are found in the user access tags.
     * @param accessTags The required access tags. '*' can be used to denote any / at least one.
     * @param userAccessTags The user access tags.
     */
    static hasAccessTags(
        accessTags: string[],
        userAccessTags: string[],
        selector: "all" | "any",
    ) {
        if (!accessTags.length) {
            return true;
        }

        if (accessTags.length === 1 && accessTags[0] === "*") {
            return !!userAccessTags.length;
        }
        if (selector === "any") {
            return intersection(userAccessTags, accessTags).length > 0;
        }

        return (
            intersection(userAccessTags, accessTags).length ===
            accessTags.length
        );
    }
}
